/**
 * Platform-tool registry helpers for the llm-agent tools field (DEV-16944).
 *
 * `GET /v1/platform-tools` enumerates the built-in (non-MCP) tool ids a
 * `toolRef` may name — a static registry, so the query is cached long.
 * These pure helpers back the picker UI and are exported for the node-env
 * test suite (same pattern as McpToolsEditor's value-transition helpers).
 */

import type { PlatformTool } from '@cortex/types';

/**
 * Picker rows: registry order, minus already-attached refs, filtered by a
 * case-insensitive substring match on id / description / group. An empty
 * query keeps everything (still minus attached).
 */
export function filterPlatformTools(
  items: PlatformTool[],
  query: string,
  attachedRefs: string[],
): PlatformTool[] {
  const attached = new Set(attachedRefs);
  const q = query.trim().toLowerCase();
  return items.filter((t) => {
    if (attached.has(t.id)) return false;
    if (q.length === 0) return true;
    return (
      t.id.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      (t.group?.toLowerCase().includes(q) ?? false)
    );
  });
}

export type ToolRefKind = 'connection' | 'platform' | 'custom' | 'unknown';

/**
 * What a toolRef resolves to, for chip styling:
 *   - 'connection' — matches an MCP connection name (rendered by the shared
 *     McpToolsEditor, not as a chip).
 *   - 'platform'   — matches a platform-tool id from the registry.
 *   - 'unknown'    — both registries loaded and the ref matches NEITHER — it
 *     will not resolve at runtime → warning affordance.
 *   - 'custom'     — we can't (yet) tell: the connections query hasn't
 *     succeeded (`registriesReady === false` — loading or errored, so
 *     `connectionNames` may be spuriously empty), or the platform registry is
 *     unavailable (`platformIds === null`, e.g. older API or failed fetch).
 *     No warning — benefit of the doubt; flagging a valid ref while a query
 *     is in flight invites the user to delete a working ref.
 */
export function classifyRef(
  ref: string,
  connectionNames: ReadonlySet<string>,
  platformIds: ReadonlySet<string> | null,
  registriesReady: boolean,
): ToolRefKind {
  if (connectionNames.has(ref)) return 'connection';
  if (!registriesReady || platformIds === null) return 'custom';
  return platformIds.has(ref) ? 'platform' : 'unknown';
}
